import { throttle, } from '@tencent/exeditor3-core';
import '@tencent/exeditor3-ui-kit/dist/toolbar-button.js';
import * as zIndex from '@tencent/exeditor3-ui-kit/dist/resizer/zIndex.js';
import { calculateDisplayRect } from './utils.js';
import { createTextarea } from './helper.js';
import { transformAttrsToActualState } from './transforms.js';
const NO_OUTLINE = 'no-outline';
const containerClassName = 'exeditor-picture';
export class PictureEditView {
    constructor(editor, node, getPos, options) {
        this.editor = editor;
        this.node = node;
        this.getPos = getPos;
        this.options = options;
        // 状态
        this.destroyed = false;
        this.imageState = {};
        this.state = {
            width: 0,
            widthPercent: 0,
        };
        this.handleReadOnlyChange = (readonly) => {
            if (this.textarea) {
                this.textarea.readOnly = readonly;
            }
            this.image.editable = !readonly;
        };
        const info = node.attrs.info || {};
        this.editor.emit('ex:picture:construct', {
            info,
            pos: getPos(),
            node,
        });
        this.loaded = Boolean(info.widthPercent);
        this.changed = Boolean(info.widthPercent);
        this.state = {
            width: 0,
            widthPercent: 0,
        };
        this.image = this.createImage(info);
        const container = document.createElement('div');
        container.classList.add(containerClassName, NO_OUTLINE);
        container.appendChild(this.image);
        if (options.hasTitle) {
            this.textarea = this.createTextarea(info.title);
            container.appendChild(this.textarea);
        }
        if (info.float) {
            container.style.float = info.float;
        }
        this.dom = container;
        this.observer = this.observeWidth(container);
        this.editor.on('readonly', this.handleReadOnlyChange);
    }
    update(node) {
        this.node = node;
        const { info = {}, uploading } = node.attrs;
        // 重新上传图片
        if (info.src !== this.image.src || uploading) {
            return false;
        }
        this.editor.emit('ex:picture:change', {
            info,
            pos: this.getPos(),
            node,
        });
        if (this.textarea && info.title !== this.textarea.value) {
            this.textarea.value = info.title;
            this.textarea.resize();
        }
        this.handleUpdate();
        return true;
    }
    selectNode() {
        this.image.active = true;
        this.dom.style.setProperty('z-index', zIndex.box.toString());
    }
    deselectNode() {
        this.image.active = false;
        this.dom.style.removeProperty('z-index');
    }
    stopEvent(event) {
        if (event.target === this.textarea) {
            return true;
        }
        return false;
    }
    destroy() {
        this.destroyed = true;
        this.editor.emit('ex:picture:destroy', {
            info: this.node.attrs.info,
            pos: this.getPos(),
            node: this.node,
        });
        this.observer.unobserve(this.dom);
        this.observer.disconnect();
        this.editor.off('readonly', this.handleReadOnlyChange);
    }
    createTextarea(title = '') {
        const handleChangeTitle = (title) => {
            const oldInfo = this.node.attrs.info;
            const oldTitle = oldInfo.title;
            if (this.destroyed)
                return;
            if (title !== oldTitle) {
                const pos = this.getPos();
                const info = { ...oldInfo, title };
                this.editor.patchNodeAttr(pos, { info });
            }
        };
        const textarea = createTextarea({
            placeholder: this.editor.t('（请输入图片标题）'),
            onChange: handleChangeTitle,
        });
        textarea.value = title;
        textarea.rows = 1;
        return textarea;
    }
    createImage(info) {
        const image = document.createElement('exeditor-picture-block-container');
        image.t = this.editor.t.bind(this.editor);
        image.src = info.src;
        image.editable = this.options.editable;
        image.allowFloat = this.options.allowFloat;
        image.addEventListener('remove', () => {
            const pos = this.getPos();
            this.editor.delete(pos, pos + 1);
        });
        image.addEventListener('restore', () => {
            this.changed = false;
            const { width, height, rotate, crop } = image;
            Object.assign(this.imageState, { width, height, rotate, crop });
            this.state.widthPercent = 0;
            if (width > this.state.width) {
                const scale = this.state.width / width;
                this.zoom(scale);
            }
            this.changeAttributes();
        });
        image.addEventListener('load', () => {
            image.style.removeProperty('display');
            image.style.removeProperty('width');
            const rect = this.dom.getBoundingClientRect();
            this.state.width = rect.width;
            // 初始化时候已经有 width percent 则需要保留
            if (info.widthPercent) {
                this.state.widthPercent = info.widthPercent;
            }
            this.imageState = transformAttrsToActualState(info, {
                container: this.dom,
                image,
                lastState: { widthPercent: info.widthPercent },
            });
            this.loaded = true;
            // 设置展示的尺寸
            const result = calculateDisplayRect(this.imageState);
            if (result.width > this.state.width) {
                const scale = this.state.width / result.width;
                this.zoom(scale);
            }
            else {
                this.zoom(1);
            }
        });
        image.addEventListener('change', () => {
            const { width, height, rotate, crop } = image;
            if (rotate !== this.imageState.rotate && this.options.resizeOnRotate) {
                // 检查是否为未编辑过的条件下进行旋转
                const isDefaultStatus = !crop && !this.state.widthPercent;
                const { width: trueWidth } = calculateDisplayRect(this.imageState);
                if (!isDefaultStatus) {
                    this.changed = true;
                    this.state.widthPercent = trueWidth / this.state.width;
                }
                Object.assign(this.imageState, { width, height, rotate, crop });
            }
            else {
                this.changed = true;
                Object.assign(this.imageState, { width, height, rotate, crop });
                const { width: trueWidth } = calculateDisplayRect(this.imageState);
                this.state.widthPercent = trueWidth / this.state.width;
            }
            this.changeAttributes();
        });
        // 先预设宽和高防止 load 更新属性时闪烁
        if (info.widthPercent) {
            image.style.width = `${info.widthPercent}%`;
        }
        else {
            Object.assign(image, {
                width: info.width,
                height: info.height,
            });
        }
        image.style.display = 'none';
        return image;
    }
    observeWidth(el) {
        const handleResizeDebounce = throttle(this.handleResize.bind(this), 15);
        let lastContainerWidth = 0;
        const observe = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.contentRect) {
                    const rectWidth = entry.contentRect.width;
                    if (lastContainerWidth !== rectWidth) {
                        lastContainerWidth = rectWidth;
                        handleResizeDebounce(entry.contentRect);
                    }
                }
            });
        });
        observe.observe(el);
        return observe;
    }
    handleResize(rect) {
        const { changed, loaded, state, imageState, image } = this;
        if (!loaded) {
            return;
        }
        const oldWidth = state.width;
        state.width = rect.width;
        image.max = rect.width;
        let scale = 1;
        if (!state.widthPercent) {
            // 未进行过尺寸变化
            const { width: boxWidth } = calculateDisplayRect(image);
            const { width: originBoxWidth } = calculateDisplayRect({
                width: image.originWidth,
                height: image.originHeight,
                rotate: imageState.rotate,
            });
            if (oldWidth < boxWidth) {
                // 处理特殊边界情况
                scale = state.width / boxWidth;
            }
            else if (state.width >= boxWidth) {
                // 溢出
                if (state.width > originBoxWidth) {
                    // 缩放到容器大小
                    scale = originBoxWidth / boxWidth;
                }
                else {
                    // 正常缩放
                    scale = state.width / oldWidth;
                }
            }
            else {
                // 溢出
                scale = state.width / boxWidth;
            }
        }
        else if (changed) {
            // 已经进行过尺寸变化
            const { width: boxWidth } = calculateDisplayRect(image);
            if (oldWidth < boxWidth) {
                // 理论上不会出现容器比已经
                scale = state.width / boxWidth;
            }
            else if (state.width >= boxWidth) {
                // 正常缩放
                scale = state.width / oldWidth;
            }
            else {
                // 溢出
                scale = state.width / boxWidth;
            }
        }
        else {
            // 常规resize/crop
            const imageWidth = image.originWidth;
            if (state.width < imageWidth) {
                state.widthPercent = 1;
                // 溢出
                scale = state.width / (imageState.width || 1);
            }
            else {
                state.widthPercent = imageWidth / state.width;
                // 正常缩放
                scale = imageWidth / (imageState.width || 1);
            }
        }
        this.zoom(scale);
    }
    handleUpdate() {
        let { imageState } = this;
        const { loaded, state, node, image, dom: container } = this;
        if (!loaded) {
            return;
        }
        // 防止溢出
        image.max = state.width;
        imageState = transformAttrsToActualState(node.attrs.info, {
            container,
            image,
            lastState: {
                ...imageState,
                widthPercent: state.widthPercent,
            },
        });
        state.widthPercent = imageState.widthPercent || 0;
        const result = calculateDisplayRect(imageState);
        // 同步状态
        this.imageState = imageState;
        // 判断是否需要调整展示尺寸
        if (result.width > state.width) {
            const scale = state.width / result.width;
            this.zoom(scale);
        }
        else {
            this.zoom(1);
        }
        // 更新容器
        this.updateContainer(imageState);
    }
    updateContainer({ float }) {
        if (float) {
            this.dom.style.float = float;
        }
        else {
            this.dom.style.removeProperty('float');
        }
    }
    // 把百分之计算成绝对的值，存到属性里面
    zoom(scale) {
        const { loaded, image, imageState } = this;
        if (!loaded) {
            return;
        }
        ['width', 'height'].forEach((key) => {
            imageState[key] = (imageState[key] || 0) * scale;
        });
        if (imageState.crop) {
            const { crop } = imageState;
            ['width', 'height', 'x', 'y'].forEach((key) => {
                crop[key] = crop[key] * scale;
            });
        }
        this.imageState = imageState;
        Object.assign(image, imageState);
    }
    // 更新节点的属性
    changeAttributes() {
        const { node, image, state } = this;
        const { width, height, rotate, crop, float } = image;
        const oldInfo = node.attrs.info;
        const info = {
            src: oldInfo.src,
            title: oldInfo.title,
            width: oldInfo.width,
            height: oldInfo.height,
            float: oldInfo.float,
            // 取最新的百分比
            widthPercent: state.widthPercent,
        };
        // 调整宽度
        if (width && width !== info.width) {
            info.aspectRatio = width / height;
        }
        info.rotate = rotate;
        if (rotate) {
            if (!state.widthPercent) {
                info.dataWidth = width;
            }
            info.aspectRatio = width / height;
        }
        if (crop) {
            info.aspectRatio = width / height;
            info.crop = {
                width: crop.width / width,
                height: crop.height / height,
                x: crop.x / width,
                y: crop.y / height,
            };
        }
        if (float) {
            info.float = float;
        }
        else {
            info.float = '';
        }
        const pos = this.getPos();
        this.editor.patchNodeAttr(pos, { info });
    }
}
//# sourceMappingURL=picture-edit-view.js.map